import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';
import type {
  Company,
  CompanyAsset,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyListResponse,
  CompanyAssetsResponse,
  MessageResponse,
  RequestConfig,
} from './types';

/**
 * Companies Module - Handles all company-related API operations
 * 
 * This module provides company management operations including CRUD operations
 * and asset management (logos and images).
 */
export class CompaniesModule extends BaseModule {
  constructor(httpClient: AxiosInstance, errorHandler: ErrorHandler) {
    super(httpClient, errorHandler);
  }

  // ============================================================================
  // Company CRUD Operations
  // ============================================================================

  /**
   * Get all companies
   * GET /api/admin/companies
   */
  async getCompanies(config?: RequestConfig): Promise<CompanyListResponse> {
    return this.get<CompanyListResponse>('/api/admin/companies', config);
  }

  /**
   * Get company by ID (admin)
   * GET /api/admin/companies/:id
   */
  async getCompanyById(id: string, config?: RequestConfig): Promise<Company> {
    return this.get<Company>(`/api/admin/companies/${id}`, config);
  }

  /**
   * Get company by ID (public - no auth required)
   * GET /api/companies/:id
   */
  async getCompanyPublic(id: string, config?: RequestConfig): Promise<Company> {
    return this.get<Company>(`/api/companies/${id}`, config);
  }

  /**
   * Create a new company
   * POST /api/admin/companies
   */
  async createCompany(data: CreateCompanyRequest, config?: RequestConfig): Promise<Company> {
    const transformedData = this.transformRequest(data);
    return this.post<Company>('/api/admin/companies', transformedData, config);
  }

  /**
   * Update an existing company
   * PUT /api/admin/companies/:id
   */
  async updateCompany(id: string, data: UpdateCompanyRequest, config?: RequestConfig): Promise<Company> {
    const transformedData = this.transformRequest(data);
    return this.put<Company>(`/api/admin/companies/${id}`, transformedData, config);
  }

  /**
   * Delete a company
   * DELETE /api/admin/companies/:id
   */
  async deleteCompany(id: string, config?: RequestConfig): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/companies/${id}`, config);
  }

  // ============================================================================
  // Company Asset Management
  // ============================================================================

  /**
   * Get all assets for a company
   * GET /api/admin/companies/:companyId/assets
   */
  async getCompanyAssets(companyId: string, config?: RequestConfig): Promise<CompanyAssetsResponse> {
    return this.get<CompanyAssetsResponse>(`/api/admin/companies/${companyId}/assets`, config);
  }

  /**
   * Upload a company asset (logo or image)
   * POST /api/admin/companies/:companyId/assets
   */
  async uploadCompanyAsset(
    companyId: string,
    file: File,
    assetType: 'logo' | 'image',
    isDefault: boolean = false,
    config?: RequestConfig
  ): Promise<CompanyAsset> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', assetType);
    formData.append('is_default', String(isDefault));

    const uploadConfig: RequestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    };

    return this.post<CompanyAsset>(
      `/api/admin/companies/${companyId}/assets`,
      formData,
      uploadConfig
    );
  }

  /**
   * Delete a company asset
   * DELETE /api/admin/companies/:companyId/assets/:assetId
   */
  async deleteCompanyAsset(companyId: string, assetId: string, config?: RequestConfig): Promise<MessageResponse> {
    return this.delete<MessageResponse>(`/api/admin/companies/${companyId}/assets/${assetId}`, config);
  }

  /**
   * Set an asset as the default for the company
   * PATCH /api/admin/companies/:companyId/assets/:assetId/set-default
   */
  async setDefaultAsset(companyId: string, assetId: string, config?: RequestConfig): Promise<CompanyAsset> {
    return this.patch<CompanyAsset>(`/api/admin/companies/${companyId}/assets/${assetId}/set-default`, {}, config);
  }

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get company with all its assets
   */
  async getCompanyWithAssets(companyId: string, config?: RequestConfig): Promise<{
    company: Company;
    assets: CompanyAsset[];
  }> {
    const [company, assets] = await Promise.all([
      this.getCompanyById(companyId, config),
      this.getCompanyAssets(companyId, config),
    ]);

    return { company, assets };
  }

  /**
   * Upload company logo and set as default
   */
  async uploadCompanyLogo(
    companyId: string,
    file: File,
    setAsDefault: boolean = true,
    config?: RequestConfig
  ): Promise<CompanyAsset> {
    return this.uploadCompanyAsset(companyId, file, 'logo', setAsDefault, config);
  }

  /**
   * Upload company image
   */
  async uploadCompanyImage(
    companyId: string,
    file: File,
    config?: RequestConfig
  ): Promise<CompanyAsset> {
    return this.uploadCompanyAsset(companyId, file, 'image', false, config);
  }

  /**
   * Get default logo for a company
   */
  async getDefaultLogo(companyId: string, config?: RequestConfig): Promise<CompanyAsset | null> {
    const assets = await this.getCompanyAssets(companyId, config);
    return assets.find(asset => asset.asset_type === 'logo' && asset.is_default) || null;
  }

  /**
   * Get all logos for a company
   */
  async getCompanyLogos(companyId: string, config?: RequestConfig): Promise<CompanyAsset[]> {
    const assets = await this.getCompanyAssets(companyId, config);
    return assets.filter(asset => asset.asset_type === 'logo');
  }

  /**
   * Get all images for a company
   */
  async getCompanyImages(companyId: string, config?: RequestConfig): Promise<CompanyAsset[]> {
    const assets = await this.getCompanyAssets(companyId, config);
    return assets.filter(asset => asset.asset_type === 'image');
  }

  /**
   * Create company with logo upload
   */
  async createCompanyWithLogo(
    companyData: CreateCompanyRequest,
    logoFile?: File,
    config?: RequestConfig
  ): Promise<{
    company: Company;
    logo?: CompanyAsset;
  }> {
    // First create the company
    const company = await this.createCompany(companyData, config);

    // If logo file is provided, upload it
    if (logoFile) {
      const logo = await this.uploadCompanyLogo(company.id, logoFile, true, config);
      return { company, logo };
    }

    return { company };
  }

  /**
   * Update company and optionally upload new logo
   */
  async updateCompanyWithLogo(
    companyId: string,
    companyData: UpdateCompanyRequest,
    logoFile?: File,
    config?: RequestConfig
  ): Promise<{
    company: Company;
    logo?: CompanyAsset;
  }> {
    // Update company data
    const company = await this.updateCompany(companyId, companyData, config);

    // If logo file is provided, upload it
    if (logoFile) {
      const logo = await this.uploadCompanyLogo(companyId, logoFile, true, config);
      return { company, logo };
    }

    return { company };
  }

  /**
   * Save an existing URL as a company asset
   * POST /api/admin/companies/:companyId/assets/save-url
   */
  async saveUrlAsCompanyAsset(
    companyId: string,
    url: string,
    assetType: 'logo' | 'image',
    isDefault: boolean = false,
    config?: RequestConfig
  ): Promise<CompanyAsset> {
    return this.post<CompanyAsset>(
      `/api/admin/companies/${companyId}/assets/save-url`,
      {
        url,
        asset_type: assetType,
        is_default: isDefault,
      },
      config
    );
  }
}
